<template>
    <ModalShell title="Add Phone" subtitle="Enter the phone number you'd like to add to your Identity Vault">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                <path
                    d="M18.25 0.25H1.75C1.35218 0.25 0.970644 0.408035 0.68934 0.68934C0.408035 0.970644 0.25 1.35218 0.25 1.75V14.5C0.25 14.8978 0.408035 15.2794 0.68934 15.5607C0.970644 15.842 1.35218 16 1.75 16H3.25V17.5C3.25 17.6989 3.32902 17.8897 3.46967 18.0303C3.61032 18.171 3.80109 18.25 4 18.25C4.19891 18.25 4.38968 18.171 4.53033 18.0303C4.67098 17.8897 4.75 17.6989 4.75 17.5V16H15.25V17.5C15.25 17.6989 15.329 17.8897 15.4697 18.0303C15.6103 18.171 15.8011 18.25 16 18.25C16.1989 18.25 16.3897 18.171 16.5303 18.0303C16.671 17.8897 16.75 17.6989 16.75 17.5V16H18.25C18.6478 16 19.0294 15.842 19.3107 15.5607C19.592 15.2794 19.75 14.8978 19.75 14.5V1.75C19.75 1.35218 19.592 0.970644 19.3107 0.68934C19.0294 0.408035 18.6478 0.25 18.25 0.25ZM18.25 14.5H1.75V1.75H18.25V7.75H16.6863C16.4982 6.63745 15.899 5.63615 15.0075 4.94452C14.116 4.25289 12.9972 3.9214 11.8729 4.01576C10.7485 4.11012 9.70059 4.62343 8.93683 5.45397C8.17307 6.28451 7.74919 7.37167 7.74919 8.5C7.74919 9.62833 8.17307 10.7155 8.93683 11.546C9.70059 12.3766 10.7485 12.8899 11.8729 12.9842C12.9972 13.0786 14.116 12.7471 15.0075 12.0555C15.899 11.3639 16.4982 10.3625 16.6863 9.25H18.25V14.5ZM13.5475 7.75C13.3824 7.46404 13.1276 7.24054 12.8225 7.11418C12.5174 6.98782 12.1792 6.96565 11.8602 7.05111C11.5413 7.13657 11.2594 7.32489 11.0584 7.58686C10.8574 7.84882 10.7485 8.1698 10.7485 8.5C10.7485 8.8302 10.8574 9.15118 11.0584 9.41314C11.2594 9.67511 11.5413 9.86343 11.8602 9.94889C12.1792 10.0344 12.5174 10.0122 12.8225 9.88582C13.1276 9.75946 13.3824 9.53596 13.5475 9.25H15.1562C14.9737 9.95699 14.5396 10.5731 13.9352 10.983C13.3309 11.3928 12.5979 11.5681 11.8735 11.4761C11.1491 11.3841 10.4832 11.0311 10.0005 10.4832C9.51782 9.93529 9.25151 9.23018 9.25151 8.5C9.25151 7.76982 9.51782 7.0647 10.0005 6.51683C10.4832 5.96895 11.1491 5.61592 11.8735 5.52391C12.5979 5.43191 13.3309 5.60724 13.9352 6.01705C14.5396 6.42686 14.9737 7.04301 15.1562 7.75H13.5475Z"
                    fill="#181D27"
                />
            </svg>
        </template>
        <div ref="phoneDivRef" class="phone-div">
            <PrimeSelect
                v-model="selectedCountry"
                :options="filteredCountryOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Country Code"
                class="w-auto !rounded"
                :panelStyle="{ width: phoneDivWidth ? `${phoneDivWidth}px` : 'auto', marginTop: '4px' }"
                :pt="{ header: { style: { paddingTop: '4px' } } }"
                filter
                filterPlaceholder="Search countries..."
                @filter="onCountryFilter"
                @show="onCountryDropdownShow"
                @hide="onCountryDropdownHide"
            >
                <template #value="slotProps">
                    <div v-if="slotProps.value && slotProps.value.code" class="flex items-center">
                        <span>{{ slotProps.value.dial_code }}</span>
                    </div>
                    <span v-else>{{ slotProps.placeholder }}</span>
                </template>

                <template #option="slotProps">
                    <div class="flex items-center">
                        <img
                            :src="`/flags/${slotProps.option.code}.svg`"
                            class="mr-2 w-4 h-4"
                            :alt="slotProps.option.name"
                            loading="lazy"
                            @error="handleFlagError"
                        />
                        <span>{{ slotProps.option.label }}</span>
                    </div>
                </template>
            </PrimeSelect>

            <prime-input-text
                v-model="phoneInput"
                class="w-full"
                type="text"
                placeholder="555-555-5555"
                @keydown="onPhoneKeydown"
                @input="onPhoneInput"
                @keydown.enter="onEnter"
                :invalid="isPhoneInvalid"
            />
        </div>
        <div v-if="isPhoneInvalid" class="validation-message">
            <span>Please enter a valid phone number.</span>
        </div>

        <div class="method-div">
            <span>Verification Method</span>
            <PrimeSelect
                class="w-full"
                v-model="selectedMethod"
                :options="methodOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Verification Method"
            />
        </div>
        <template #footer>
            <PrimeButton @click="closeDialog" class="p-btn-root_secondary" label="Cancel" />

            <PrimeButton
                label="Send Verification Code"
                variant="primary"
                :disabled="!isValidPhoneNumber(phoneInput) || !selectedCountry || !selectedMethod"
                @click="sendVerificationCode"
                :loading="isSendingVerificationCode"
            />
        </template>
    </ModalShell>
</template>
<script setup lang="ts">
import countries from '@/assets/json/countries.json';

import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { ModalNames } from '~/types/modals';

const { showError } = useGlobalToast();

const countryOptions = countries.map((c) => ({
    label: `${c.name} (${c.dial_code})`,
    value: c,
    code: c.code.toLowerCase(),
    name: c.name,
    dial_code: c.dial_code,
}));

const searchQuery = ref('');
const selectedCountry = ref(countryOptions.find((c) => c.value.code.toLowerCase() === 'us'));

// Template ref for phone-div to get its width for the dropdown panel
const phoneDivRef = ref<HTMLElement | null>(null);
const phoneDivWidth = ref(0);

// Update width when dropdown opens
const updatePanelWidth = () => {
    if (phoneDivRef.value) {
        phoneDivWidth.value = phoneDivRef.value.offsetWidth;
    }
};

// Filtered countries based on search query
const filteredCountryOptions = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
        return countryOptions;
    }

    const query = searchQuery.value.toLowerCase().trim();
    
    const filtered = countryOptions.filter(country => {
        const countryName = country.name.toLowerCase();
        const dialCode = country.dial_code.toLowerCase();
        const label = country.label.toLowerCase();
        
        return countryName.includes(query) || 
               dialCode.includes(query) || 
               label.includes(query);
    });

    // Sort by relevance
    return filtered.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aDialCode = a.dial_code.toLowerCase();
        const bDialCode = b.dial_code.toLowerCase();
        
        // Exact name matches first
        if (aName === query && bName !== query) return -1;
        if (bName === query && aName !== query) return 1;
        
        // Exact dial code matches
        if (aDialCode === query && bDialCode !== query) return -1;
        if (bDialCode === query && aDialCode !== query) return 1;
        
        // Name starts with query
        if (aName.startsWith(query) && !bName.startsWith(query)) return -1;
        if (bName.startsWith(query) && !aName.startsWith(query)) return 1;
        
        // Dial code starts with query
        if (aDialCode.startsWith(query) && !bDialCode.startsWith(query)) return -1;
        if (bDialCode.startsWith(query) && !aDialCode.startsWith(query)) return 1;
        
        // Alphabetical fallback
        return aName.localeCompare(bName);
    });
});

// Handle filter input from PrimeSelect
const onCountryFilter = (event: { value: string }) => {
    searchQuery.value = event.value || '';
};

function onCountryDropdownShow() {
    // Reset search when dropdown opens
    searchQuery.value = '';
    // Update panel width to match phone-div
    updatePanelWidth();
}

function onCountryDropdownHide() {
    // Reset search when dropdown closes  
    searchQuery.value = '';
}

// Handle flag image loading errors
const handleFlagError = (event: Event) => {
    const img = event.target as HTMLImageElement;
    // Hide the image if it fails to load
    img.style.display = 'none';
};

const onPhoneKeydown = (e: KeyboardEvent) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

    // Allow Ctrl/Cmd + A (select all), C (copy), V (paste), X (cut)
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
        return;
    }

    // Allow the key if it's in the allowed list
    if (allowedKeys.includes(e.key)) {
        return;
    }

    // Prevent if not a number (0-9)
    if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        return;
    }

};

// Format phone number with dashes (e.g., 555-555-5555)
const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else if (digits.length <= 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
        // For numbers longer than 10 digits, format first 10 then append rest
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}-${digits.slice(10)}`;
    }
};

// Validate phone number (minimum 7 digits for a valid phone number)
const isValidPhoneNumber = (value: string): boolean => {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7;
};

// Check if user has started typing (for showing validation)
const hasPhoneInput = computed(() => phoneInput.value.replace(/\D/g, '').length > 0);

// Check if phone is invalid (has input but less than minimum digits)
const isPhoneInvalid = computed(() => hasPhoneInput.value && !isValidPhoneNumber(phoneInput.value));

const onPhoneInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    // Remove all non-numeric characters
    const digits = target.value.replace(/\D/g, '');
    // Apply formatting
    phoneInput.value = formatPhoneNumber(digits);
};

const onEnter = (e: KeyboardEvent) => {
    if (isValidPhoneNumber(phoneInput.value) && selectedCountry.value && selectedMethod.value && !isSendingVerificationCode.value) {
        sendVerificationCode();
    }
};

const phoneInput = ref('');

const methodOptions = [
    { label: 'SMS Text Message', value: 'sms' },
    { label: 'Phone Call', value: 'call' }
];

const selectedMethod = ref(''); 



const dialogRef = getDialogRef(ModalNames.ADD_PHONE);

// Check if we're opening from an exposure context and pre-populate the phone data
const dialogData = dialogRef.value?.data as any;

if (dialogData?.phone_number && dialogData?.country_code) {
    // Format the pre-populated phone number
    const digits = dialogData.phone_number.replace(/\D/g, '');
    phoneInput.value = formatPhoneNumber(digits);
    // Find the country code in the countryOptions list
    const country = countryOptions.find(c => c.dial_code === dialogData.country_code);
    if (country) {
        selectedCountry.value = country;
    }
}

const closeDialog = () => {
    dialogRef.value.close();
};

const personStore = usePersonStore();
const isSendingVerificationCode = ref(false);

const normalizePhoneNumber = (phoneNumber: string, countryCode: string): string => {
    let normalized = phoneNumber.trim();
    
    // For Philippine numbers (country code 63)
    if (countryCode === '63' || countryCode === '+63') {
        // Remove leading zero if present
        // PH numbers: 0917-123-4567 becomes 917-123-4567
        if (normalized.startsWith('0')) {
            normalized = normalized.substring(1);
        }
    }
    
    // Remove all non-digit characters for comparison
    normalized = normalized.replace(/\D/g, '');
    
    return normalized;
};

const sendVerificationCode = async () => {
    isSendingVerificationCode.value = true;

    const selectedMethodValue = selectedMethod.value === 'sms' ? true : false;

     // Get the country code without the + sign
    const countryCodeValue = selectedCountry.value?.dial_code?.replace(/^\+/, '') || '1';

    // Normalize the input phone number
    const normalizedInput = normalizePhoneNumber(phoneInput.value, countryCodeValue);

    const requestData = {
        phone_number: phoneInput.value,
        country_code: selectedCountry.value?.dial_code?.replace(/^\+/, '') || '1',
        method: selectedMethod.value,
        fromExposure: dialogData?.fromExposure || false, // Pass through the fromExposure flag
        exposureData: dialogData?.exposureData, // Pass through the exposure data for suppression
    };

    // Check if phone number already exists
    const existingPhone = personStore.person?.phone_numbers?.find(phone => {
        const existingNormalized = normalizePhoneNumber(
            phone.phone_number, 
            phone.country_code
        );
        const existingCountryCode = phone.country_code.replace(/^\+/, '');
        
        // Compare normalized numbers and country codes
        return existingNormalized === normalizedInput && 
               existingCountryCode === countryCodeValue;
    });

    if (existingPhone) {
        // Add 1 second delay to show loading
        await new Promise(resolve => setTimeout(resolve, 1000));
        showError('Error', 'This phone number already exists in your vault');
        isSendingVerificationCode.value = false;
        return;
    }

        try {
            await personStore.sendPhoneVerificationCode(selectedMethodValue, requestData);
            closeDialog();
        } catch (error) {
            // Keep dialog open on error so user can retry
            console.error('Failed to send verification code:', error);
        } finally {
            isSendingVerificationCode.value = false;
        }
   
    
    
};
</script>
<style lang="scss" scoped>
.method-div {
    @apply flex flex-col items-start gap-2 self-stretch mt-2 sm:w-auto w-full;

    span {
        @apply text-zinc-950 font-inter text-sm font-semibold leading-5;
    }
}
.footer-actions {
    .p-btn-root_primary {
        @apply sm:px-3 px-1 py-2;
    }
}
.phone-div {
    @apply flex flex-row gap-2.5 w-full;
}
.validation-message {
    @apply flex gap-1 mt-1 w-full;

    span {
        @apply text-red-600 font-inter text-sm font-normal leading-5;
    }
}
</style>